import { useEffect, useState } from "react";
import { CalendarCheck2, CalendarPlus, Clock3 } from "lucide-react";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs/tabs.tsx";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import type { PageProps } from "../../types/page.ts";
import {
  LEAVE_ATTENDANCE_TABS,
  LeaveApprovalTab,
  LeaveBalanceTab,
  PunchDetailsTab,
  TAB_FINAL_APPROVAL,
  TAB_LEAVE_BALANCE,
  TAB_PUNCH_DETAILS,
  useLeaveAttendanceEmployees,
  type LeaveAttendanceTabId,
} from "../../components/features/leave-attendance-management/index.ts";
import { showError } from "../../utils/toast.ts";
import { useSqeLabs } from "../../hooks/use-sqe-labs.ts";
import styles from "./leave-attendance-management-page.module.css";

const TAB_ICONS: Record<LeaveAttendanceTabId, typeof CalendarPlus> = {
  "leave-balance": CalendarPlus,
  "final-approval": CalendarCheck2,
  "punch-details": Clock3,
};


const HERO_ILLUSTRATION = "/assets/images/organization_structure_icon.png";

export default function LeaveAttendanceManagementPage({
  title,
  description,
  pageMeta,
}: PageProps) {
  const { setBreadcrumbs } = useBreadcrumbs();
  const isSqe = useSqeLabs();
  const [activeTab, setActiveTab] =
    useState<LeaveAttendanceTabId>(TAB_LEAVE_BALANCE);
  const {
    employees,
    employeeOptions,
    loading: employeesLoading,
    error: employeesError,
  } = useLeaveAttendanceEmployees(isSqe);

  const pageTitle = pageMeta?.title || title || "Leave & Attendance Management";
  const pageDescription =
    pageMeta?.description ||
    description ||
    "Manage leave balances, final leave approvals, and employee punch details in one place.";

  const heroTitle = "Leave & Attendance Management";
  const heroDescription =
    "Manage leave balances, final leave approvals, and employee punch details from a single workspace.";

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [pageTitle, setBreadcrumbs]);

  useEffect(() => {
    if (employeesError) showError(employeesError);
  }, [employeesError]);

  return (
    <PageShell>
      <section className={styles.hero} aria-labelledby="leave-attendance-title">
        <div className={styles.heroRow}>
          <div className={styles.copy}>
            <h1 id="leave-attendance-title" className="heading-lg">
              {heroTitle}
            </h1>
            <p className="sub-heading-sm">{heroDescription}</p>
          </div>
          <div className={styles.art} aria-hidden>
            <img
              src={HERO_ILLUSTRATION}
              alt=""
              className={styles.heroImg}
              width={320}
              height={160}
              decoding="async"
            />
          </div>
        </div>
      </section>

      <Card className={styles.tableCard} padding="5xl">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as LeaveAttendanceTabId)}
          idPrefix="leave-attendance-management-tabs"
            variant="card"
        >
          <TabsList className={styles.tabsList}>
            {LEAVE_ATTENDANCE_TABS.map((tab) => {
              const Icon = TAB_ICONS[tab.id];
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className={styles.tabTrigger}
                >
                  <span className={styles.tabTriggerInner}>
                    <Icon size={16} aria-hidden />
                    {tab.label}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value={TAB_LEAVE_BALANCE} className={styles.tabPanel}>
            <LeaveBalanceTab
              employeeOptions={employeeOptions}
              employeesLoading={employeesLoading}
            />
          </TabsContent>

          <TabsContent value={TAB_FINAL_APPROVAL} className={styles.tabPanel}>
            <LeaveApprovalTab />
          </TabsContent>

          <TabsContent value={TAB_PUNCH_DETAILS} className={styles.tabPanel}>
            <PunchDetailsTab
              employees={employees}
              employeeOptions={employeeOptions}
              employeesLoading={employeesLoading}
            />
          </TabsContent>
        </Tabs>
      </Card>
    </PageShell>
  );
}

